import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, CreditCardIcon, KeyRoundIcon, LockIcon, ShieldCheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { createOrderReference, formatEuro, requestPayment, type PaymentMethod, type PaymentResult } from '../utils/payments';
const PRICE = 350;
const PRODUCT = 'Ecosistema Digital · Proyecto completo';
const INCLUDED = ['Página web profesional a medida', 'Carta digital lista para consultar', 'Gestión y mantenimiento incluidos'];
type FormErrors = Partial<Record<'name' | 'email' | 'phone' | 'card' | 'expiry' | 'cvc' | 'zip', string>>;
function formatCardNumber(value: string) {
  return value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function formatExpiry(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}
export function CheckoutPage() {
  const orderReference = useMemo(() => createOrderReference(), []);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    card: '',
    expiry: '',
    cvc: '',
    zip: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'processing' | 'paid'>('idle');
  const [payment, setPayment] = useState<PaymentResult | null>(null);
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);
  const update = (field: keyof typeof form, value: string) => {
    setForm(current => ({
      ...current,
      [field]: value
    }));
    setErrors(current => {
      if (!current[field as keyof FormErrors]) return current;
      const next = {
        ...current
      };
      delete next[field as keyof FormErrors];
      return next;
    });
  };
  const validate = (method: PaymentMethod): FormErrors => {
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = 'Indica tu nombre';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Email no válido';
    if (form.phone.replace(/\D/g, '').length < 9) next.phone = 'Teléfono no válido';
    if (method === 'creditcard') {
      if (form.card.replace(/\D/g, '').length < 15) next.card = 'Número incompleto';
      if (form.expiry.replace(/\D/g, '').length < 4) next.expiry = 'Caducidad incompleta';
      if (form.cvc.replace(/\D/g, '').length < 3) next.cvc = 'CVC incompleto';
      if (!form.zip.trim()) next.zip = 'Código postal requerido';
    }
    return next;
  };
  const pay = async (method: PaymentMethod) => {
    const validation = validate(method);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setStatus('processing');
    const result = await requestPayment({
      amount: PRICE,
      currency: 'EUR',
      description: PRODUCT,
      orderReference,
      method,
      customer: {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim()
      }
    });
    setPayment(result);
    setStatus('paid');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const fieldClass = (hasError: boolean) => `w-full bg-white px-4 py-3.5 text-[15px] text-black placeholder:text-neutral-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${hasError ? 'border border-red-500' : 'border border-black/25'}`;
  return <div className="min-h-screen bg-black px-5 py-8 sm:px-8 sm:py-14" data-id="element-186" data-mp="24mt35koc3myp">
      <div className="mx-auto w-full max-w-5xl" data-id="element-187" data-mp="24kyga0ti5krz">
        <div className="mb-6 flex items-center justify-between" data-id="element-188" data-mp="1znor8ma0zb4o">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-400 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-189" data-mp="3g27w8ddf9uoj">
            <ArrowLeftIcon size={14} aria-hidden="true" data-id="element-190" data-mp="a9qifl2uo63a" />
            Volver
          </Link>
          <span className="inline-flex items-center gap-2.5 text-white" data-id="element-191" data-mp="f78vnnnwh7h3">
            <BrandMark size={17} data-id="element-192" data-mp="k51xk4i67fbk" />
            <span className="text-[12px] font-semibold tracking-[0.16em]" data-id="element-193" data-mp="k86b3shiznew">
              GROWTHLY
            </span>
          </span>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-2 border-white bg-white" data-id="element-194" data-mp="t7jnasx1aq3b">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 bg-black px-6 py-5 sm:px-9 sm:py-6" data-id="element-195" data-mp="17xqta95uugn4">
            <h1 className="text-2xl font-medium tracking-[-0.03em] text-white sm:text-3xl" data-id="element-196" data-mp="1gw2u140fquta">
              {status === 'paid' ? 'Pago confirmado' : 'Completa tu pedido'}
            </h1>
            <p className="text-xl font-medium tabular-nums text-white/70 sm:text-2xl" data-id="element-197" data-mp="2xct7kqbn0e7d">
              #{orderReference}
            </p>
          </div>

          {status === 'paid' && payment ? <div className="px-6 py-10 sm:px-9 sm:py-12" data-id="element-198" data-mp="2sfjixcw2o6kl">
              <div className="mx-auto max-w-xl" data-id="element-199" data-mp="2nfx4x18f6maq">
                <span className="flex h-12 w-12 items-center justify-center border-2 border-black text-black" data-id="element-200" data-mp="3l24c8iw3ysqs">
                  <CheckIcon size={22} aria-hidden="true" data-id="element-201" data-mp="3g2rf95biw5ah" />
                </span>
                <h2 className="mt-7 text-3xl font-medium tracking-[-0.04em] text-black" data-id="element-202" data-mp="3bckrt8sqyqtt">
                  Tu proyecto está activado.
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-neutral-600" data-id="element-203" data-mp="36cyekzcx5al0">
                  Hemos registrado el pago de {formatEuro(PRICE)} y enviaremos
                  la confirmación a{' '}
                  <span className="font-medium text-black" data-id="element-204" data-mp="2v6j3azkq8i2">
                    {form.email.trim()}
                  </span>
                  . Nos pondremos en contacto para empezar con el contexto del
                  proyecto.
                </p>
                <dl className="mt-9 border-t border-black/15" data-id="element-205" data-mp="1nf1x6ubapji5">
                  {[['Referencia', `#${payment.orderReference}`], ['Identificador de pago', payment.id], ['Importe', formatEuro(PRICE)], ['Método', payment.method === 'creditcard' ? 'Tarjeta' : payment.method === 'applepay' ? 'Apple Pay' : 'Google Pay']].map(([label, value]) => <div key={label} className="flex items-center justify-between gap-4 border-b border-black/15 py-3.5" data-id="element-206" data-mp="2ytp75bnz3lyl">
                      <dt className="text-[13px] text-neutral-500" data-id="element-207" data-mp="3dwgsy4ommtfc">{label}</dt>
                      <dd className="text-[13px] font-medium tabular-nums text-black" data-id="element-208" data-mp="38xvtw8l42fsh">
                        {value}
                      </dd>
                    </div>)}
                </dl>
                <Link to="/" className="mt-9 flex w-full items-center justify-center gap-2 bg-black px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-209" data-mp="3hytx4onq6ja0">
                  Volver al inicio
                </Link>
              </div>
            </div> : <div className="grid lg:grid-cols-[1.15fr_0.85fr]" data-id="element-210" data-mp="5f2lea76wh56">
              {/* ---------------- Payment ---------------- */}
              <div className="px-6 py-8 sm:px-9 sm:py-10" data-id="element-211" data-mp="278thuy4mpj41">
                <h2 className="text-lg font-medium tracking-[-0.02em] text-black" data-id="element-212" data-mp="27acyipu75w6c">
                  Tus datos
                </h2>
                <div className="mt-5 space-y-4" data-id="element-213" data-mp="30sb970uiz0vs">
                  <label className="block" data-id="element-214" data-mp="30r05f112xt4o">
                    <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-215" data-mp="35j9vqi63wv7h">
                      Nombre completo
                    </span>
                    <input value={form.name} onChange={event => update('name', event.target.value)} className={fieldClass(Boolean(errors.name))} placeholder="Nombre y apellidos" autoComplete="name" data-id="element-216" data-mp="39kd4kkpqlqz7" />
                    {errors.name && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-217" data-mp="2qvgc8mdwh8jm">
                        {errors.name}
                      </span>}
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-218" data-mp="63d4a622ukm0">
                    <label className="block" data-id="element-219" data-mp="65z9hz1hwo12">
                      <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-220" data-mp="g71ojolym3zj">
                        Correo electrónico
                      </span>
                      <input type="email" value={form.email} onChange={event => update('email', event.target.value)} className={fieldClass(Boolean(errors.email))} placeholder="tu@correo.com" autoComplete="email" data-id="element-221" data-mp="6i1os2zmv4ka" />
                      {errors.email && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-222" data-mp="3jpuy7qbma8of">
                          {errors.email}
                        </span>}
                    </label>
                    <label className="block" data-id="element-223" data-mp="1hwf8qwr9mzv1">
                      <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-224" data-mp="1hv44kvtwrq3e">
                        Teléfono
                      </span>
                      <input type="tel" value={form.phone} onChange={event => update('phone', event.target.value)} className={fieldClass(Boolean(errors.phone))} placeholder="+34 600 000 000" autoComplete="tel" data-id="element-225" data-mp="17vfgf047yz36" />
                      {errors.phone && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-226" data-mp="p6ijsplcnuhs">
                          {errors.phone}
                        </span>}
                    </label>
                  </div>
                </div>

                <h2 className="mt-10 text-lg font-medium tracking-[-0.02em] text-black" data-id="element-227" data-mp="u4twd8314j3p">
                  Pagar con tarjeta
                </h2>
                <div className="mt-5 space-y-4" data-id="element-228" data-mp="6dwtslx4iy54">
                  <label className="block" data-id="element-229" data-mp="1h6fa30ywalt">
                    <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-230" data-mp="1g513ponn7zv">
                      Número de tarjeta
                    </span>
                    <span className="relative block" data-id="element-231" data-mp="2hv9qk7vin1ab">
                      <input inputMode="numeric" value={form.card} onChange={event => update('card', formatCardNumber(event.target.value))} className={`${fieldClass(Boolean(errors.card))} pr-14`} placeholder="4242 4242 4242 4242" autoComplete="cc-number" data-id="element-232" data-mp="2hwtcadzxy4jh" />
                      <CreditCardIcon size={18} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-233" data-mp="2mtbcd0kbteph" />
                    </span>
                    {errors.card && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-234" data-mp="1gbo64nh102wz">
                        {errors.card}
                      </span>}
                  </label>
                  <div className="grid gap-4 sm:grid-cols-3" data-id="element-235" data-mp="3d4lqudw79zwe">
                    <label className="block" data-id="element-236" data-mp="38cby6q3palqf">
                      <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-237" data-mp="38b0q4e382rti">
                        MM/AA
                      </span>
                      <input inputMode="numeric" value={form.expiry} onChange={event => update('expiry', formatExpiry(event.target.value))} className={fieldClass(Boolean(errors.expiry))} placeholder="04/29" autoComplete="cc-exp" data-id="element-238" data-mp="2zdgnp1tmiphb" />
                      {errors.expiry && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-239" data-mp="jl196zhukzr8">
                          {errors.expiry}
                        </span>}
                    </label>
                    <label className="block" data-id="element-240" data-mp="26gz3iqklopwp">
                      <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-241" data-mp="26fnzcpn8tg52">
                        CVC
                      </span>
                      <span className="relative block" data-id="element-242" data-mp="2f99cy5no8rcw">
                        <input inputMode="numeric" value={form.cvc} onChange={event => update('cvc', event.target.value.replace(/\D/g, '').slice(0, 4))} className={`${fieldClass(Boolean(errors.cvc))} pr-12`} placeholder="123" autoComplete="cc-csc" data-id="element-243" data-mp="1ik1rexn1yi8z" />
                        <KeyRoundIcon size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-244" data-mp="3812uq1jdpkw7" />
                      </span>
                      {errors.cvc && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-245" data-mp="3bh5pnzumcm3p">
                          {errors.cvc}
                        </span>}
                    </label>
                    <label className="block" data-id="element-246" data-mp="3khvg0o141ka1">
                      <span className="mb-1.5 block text-[12px] font-medium text-neutral-600" data-id="element-247" data-mp="3kh2imdo1k74k">
                        Código postal
                      </span>
                      <input inputMode="numeric" value={form.zip} onChange={event => update('zip', event.target.value)} className={fieldClass(Boolean(errors.zip))} placeholder="28001" autoComplete="postal-code" data-id="element-248" data-mp="3u6vk2lugz5v3" />
                      {errors.zip && <span className="mt-1.5 block text-[12px] text-red-600" data-id="element-249" data-mp="vmiybq409adl">
                          {errors.zip}
                        </span>}
                    </label>
                  </div>
                </div>

                <div className="mt-8 border-t border-black/15 pt-7" data-id="element-250" data-mp="lr90zfojxr65">
                  <p className="mb-4 text-[12px] font-medium text-neutral-600" data-id="element-251" data-mp="7wf0jzdi04ui">
                    O paga en un paso
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2" data-id="element-252" data-mp="3u57w6f3p98il">
                    <button type="button" onClick={() => pay('applepay')} disabled={status === 'processing'} className="flex items-center justify-center gap-2 bg-black px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:opacity-60" data-id="element-253" data-mp="3u711nr2ktmp0">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true" data-id="element-254" data-mp="1qvcru5keicdi">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-255" data-mp="2em9u0qme9vbk" />
                      </svg>
                      Apple Pay
                    </button>
                    <button type="button" onClick={() => pay('googlepay')} disabled={status === 'processing'} className="flex items-center justify-center gap-2 bg-black px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:opacity-60" data-id="element-256" data-mp="u7g8wu1hr4z4">
                      <span className="text-base font-semibold leading-none" data-id="element-257" data-mp="3emvirtww5r7y">
                        G
                      </span>
                      Google Pay
                    </button>
                  </div>
                  <p className="mt-5 flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-258" data-mp="3oif2lhy6kzgc">
                    <LockIcon size={13} aria-hidden="true" data-id="element-259" data-mp="3tjupzgntzi1w" />
                    Pago procesado por Mollie. No almacenamos datos de tarjeta.
                  </p>
                </div>
              </div>

              {/* ---------------- Summary ---------------- */}
              <aside className="border-t-2 border-black/15 px-6 py-8 sm:px-9 sm:py-10 lg:border-l-2 lg:border-t-0" data-id="element-260" data-mp="2365a844zwnb6">
                <h2 className="text-lg font-medium tracking-[-0.02em] text-black" data-id="element-261" data-mp="1k9deujxxx366">
                  Resumen del pedido
                </h2>
                <div className="mt-6 flex items-start justify-between gap-4 border-b border-black/15 pb-4" data-id="element-262" data-mp="1f9r0u8aafiwb">
                  <p className="text-[14px] leading-relaxed text-black" data-id="element-263" data-mp="1p1d85tji8xts">
                    Ecosistema Digital
                    <span className="mt-1 block text-[13px] text-neutral-500" data-id="element-264" data-mp="11bal5rug6i21">
                      Pago único · Proyecto completo
                    </span>
                  </p>
                  <p className="shrink-0 text-[14px] font-medium tabular-nums text-black" data-id="element-265" data-mp="1ach4s9se88zi">
                    {formatEuro(PRICE)}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-black/15 py-4" data-id="element-266" data-mp="15c2vypiywv0f">
                  <p className="text-[13px] text-neutral-500" data-id="element-267" data-mp="2ltqo8wbiv10t">IVA</p>
                  <p className="text-[13px] text-neutral-600" data-id="element-268" data-mp="2grrhrdkhjnzn">Incluido</p>
                </div>
                <div className="flex items-baseline justify-between gap-4 py-5" data-id="element-269" data-mp="2p8ihd0cvn4ju">
                  <p className="text-[15px] font-medium text-black" data-id="element-270" data-mp="2tbf2oytsxerh">Total</p>
                  <p className="text-2xl font-medium tabular-nums tracking-[-0.03em] text-black" data-id="element-271" data-mp="2fbdsutvk6wzv">
                    {formatEuro(PRICE)}
                  </p>
                </div>

                <button type="button" onClick={() => pay('creditcard')} disabled={status === 'processing'} className="flex w-full items-center justify-center gap-2 bg-black px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-wait disabled:opacity-60" data-id="element-272" data-mp="2kcbhnpb2qpac">
                  {status === 'processing' ? <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" data-id="element-273" data-mp="3vrqe8rzdff4x" />
                      Procesando
                    </> : <>
                      <LockIcon size={15} aria-hidden="true" data-id="element-274" data-mp="1hdpz01s1ojj1" />
                      Pagar {formatEuro(PRICE)}
                    </>}
                </button>

                <ul className="mt-8 space-y-3 border-t border-black/15 pt-6" data-id="element-275" data-mp="17ftggows2maw">
                  {INCLUDED.map(item => <li key={item} className="flex gap-3 text-[13px] leading-relaxed text-neutral-600" data-id="element-276" data-mp="1ce3auxaohivg">
                      <CheckIcon size={15} className="mt-0.5 shrink-0 text-black" aria-hidden="true" data-id="element-277" data-mp="13gb3cfb77fim" />
                      {item}
                    </li>)}
                </ul>
                <p className="mt-6 flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-278" data-mp="37yiqw2ng56f8">
                  <ShieldCheckIcon size={14} aria-hidden="true" data-id="element-279" data-mp="32zpe6cfzhjg4" />
                  Devolución íntegra antes de la primera propuesta.
                </p>
              </aside>
            </div>}
        </motion.div>

        <p className="mt-6 text-center text-[12px] text-neutral-500" data-id="element-280" data-mp="3by19usvpj7ff">
          ¿Dudas antes de pagar?{' '}
          <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="border-b border-white/40 text-neutral-300 transition-colors hover:border-white hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-281" data-mp="3qqbk9y6fgtnh">
            Escríbenos al +34 631 01 25 92
          </a>
        </p>
      </div>
    </div>;
}